import React, { useState, useEffect, useMemo } from "react";
import {
    FaPlus,
    FaMagnifyingGlass,
    FaBars,
} from "react-icons/fa6";
import { useNavigate } from "react-router-dom";
import { DashboardLayout } from "../../../components/layouts/DashboardLayout";
import { GenieAcsMobileCardListSection } from "./sections/MobileCardListSection";
import { GenieAcsDesktopTableSection } from "./sections/DesktopTable";
import { useHookGenieAcs } from "../../../hooks/useHookGenieAcs";


export const GenieAcs = () => {
    const navigate = useNavigate();
    /* ---------- State ---------- */
    const [showSidebar, setShowSidebar] = useState(true);
    const [devices, setDevices] = useState([]);

    // Filter State
    const [searchTerm, setSearchTerm] = useState("");
    const [filterStatus, setFilterStatus] = useState("");
    const [filterModel, setFilterModel] = useState("");

    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    // Modal State
    const [modalConfig, setModalConfig] = useState({
        isOpen: false,
        type: null, // 'reboot' | 'delete'
        data: null
    });

    const {
        data,
        meta,
        loading,
        refreshData,
        keyword,
        setKeyword,

        page,
        setPage,
        limit,
        setLimit,
        sort,
        setSort,
    } = useHookGenieAcs();


    /* ---------- Mock Data Generator ---------- */
    useEffect(() => {
        setTimeout(() => {
            const names = ["Budi Santoso", "Siti Aminah", "Joko Anwar", "Rina Wati", "Agus Setiawan", "Dewi Sartika", "Eko Patrio", "Fanny Fadillah"];
            const mockData = Array.from({ length: 45 }, (_, i) => {
                const id = i + 1;
                const isOnline = Math.random() > 0.3;
                const rxPower = -(Math.random() * 20 + 5); // Random antara -5 s/d -25 dBm
                return {
                    _id: `DSHW100${id}`, // ID Internal untuk routing
                    customerId: `CUST-100${id}`,
                    customerName: names[i % names.length] + ` (${id})`,
                    ip: `192.168.100.${10 + (id % 240)}`,
                    model: ['ZTE-F609', 'Huawei-HG8245H', 'Nokia-G-240W-C'][Math.floor(Math.random() * 3)],
                    rxPower: parseFloat(rxPower.toFixed(2)),
                    lastInform: new Date(Date.now() - Math.floor(Math.random() * 1000000000)).toISOString(),
                    status: isOnline ? 'online' : 'offline',
                    tags: ['OLT-A', 'OLT-B'][Math.floor(Math.random() * 2)]
                };
            });
            setDevices(mockData);
            // setLoading(false);
        }, 1500);
    }, []);

    /* ---------- Client-side Filter ---------- */
    const filteredData = useMemo(() => {
        return devices.filter((item) => {
            const matchesSearch =
                item.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.customerId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.ip?.includes(searchTerm.toLowerCase());

            const matchesStatus = filterStatus === "" || item.status === filterStatus;
            const matchesModel = filterModel === "" || item.model === filterModel;

            return matchesSearch && matchesStatus && matchesModel;
        });
    }, [devices, searchTerm, filterStatus, filterModel]);

    /* ---------- Extract Filters Options ---------- */
    const models = useMemo(() => [...new Set(devices.map((i) => i.model))], [devices]);

    /* ---------- Pagination Logic ---------- */
    const totalPages = Math.ceil(filteredData.length / itemsPerPage);
    const currentData = filteredData.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) setCurrentPage(page);
    };

    /* ---------- Handlers ---------- */
    const handleAction = (type, item) => {
        setModalConfig({ isOpen: true, type, data: item });
    };

    const confirmAction = () => {
        if (modalConfig.type === 'delete') {
            setDevices(devices.filter(d => d._id !== modalConfig.data._id));
        } else if (modalConfig.type === 'reboot') {
            setDevices(devices.map(d =>
                d._id === modalConfig.data._id ? { ...d, status: 'rebooting' } : d
            ));
            alert(`Command REBOOT sent to ${modalConfig.data.customerId}`);
        }
        setModalConfig({ isOpen: false, type: null, data: null });
    };

    return (
        <>
            <DashboardLayout showSidebar={showSidebar} setShowSidebar={setShowSidebar}>
                <main className=" flex flex-col transition-all duration-300">
                    {/* Header */}
                    <header className="h-16 bg-slate-900/80 backdrop-blur-md border-b border-slate-700/50 sticky top-0 z-30 flex items-center justify-between px-4 lg:px-6">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => setShowSidebar(true)}
                                className="lg:hidden p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg"
                            >
                                <FaBars />
                            </button>
                            <h2 className="text-lg font-bold">CPE Devices</h2>
                            <span className="hidden sm:inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">
                                {filteredData.length} Pelanggan
                            </span>
                        </div>
                        <button className="bg-cyan-600 hover:bg-cyan-500 text-white text-sm font-medium px-4 py-2 rounded-lg flex items-center gap-2 transition-colors shadow-lg shadow-cyan-900/20">
                            <FaPlus className="text-xs" />
                            <span className="hidden sm:inline">Add Device</span>
                        </button>
                    </header>

                    {/* Content */}
                    <div className="p-4 lg:p-6 space-y-6 mx-auto w-full">

                        {/* Filter Section */}
                        <section
                            className="rounded-2xl p-4 sm:p-5"
                            style={{
                                background: "rgba(15, 23, 42, 0.6)",
                                border: "1px solid rgba(51, 65, 85, 0.4)",
                                backdropFilter: "blur(12px)",
                            }}
                        >
                            <div className="flex flex-col lg:flex-row lg:items-end gap-4">
                                <div className="w-full lg:max-w-xs flex-shrink-0">
                                    <label className="block text-xs font-semibold text-slate-400 mb-1.5 uppercase tracking-wider">
                                        Cari Pelanggan
                                    </label>
                                    <div className="relative">
                                        <FaMagnifyingGlass className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 text-sm" />
                                        <input
                                            type="text"
                                            placeholder="Nama, ID Pelanggan, atau IP..."
                                            value={keyword}
                                            onChange={(e) => {
                                                setKeyword(e.target.value);
                                                setCurrentPage(1);
                                            }}
                                            className="w-full pl-10 pr-4 py-2.5 text-sm rounded-xl bg-slate-800/60 border border-slate-600/40 text-slate-100 placeholder-slate-500 outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all"
                                        />
                                    </div>
                                </div>

                                <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-xs font-semibold text-slate-400 mb-1.5 uppercase tracking-wider">
                                            Status
                                        </label>
                                        <select
                                            value={filterStatus}
                                            onChange={(e) => {
                                                setFilterStatus(e.target.value);
                                                setCurrentPage(1);
                                            }}
                                            className="w-full px-3 py-2.5 text-sm rounded-xl bg-slate-800/60 border border-slate-600/40 text-slate-100 outline-none focus:border-cyan-500/50 cursor-pointer appearance-none"
                                        >
                                            <option value="">All Status</option>
                                            <option value="online">Online</option>
                                            <option value="offline">Offline</option>
                                            <option value="rebooting">Rebooting</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-slate-400 mb-1.5 uppercase tracking-wider">
                                            Model
                                        </label>
                                        <select
                                            value={filterModel}
                                            onChange={(e) => {
                                                setFilterModel(e.target.value);
                                                setCurrentPage(1);
                                            }}
                                            className="w-full px-3 py-2.5 text-sm rounded-xl bg-slate-800/60 border border-slate-600/40 text-slate-100 outline-none focus:border-cyan-500/50 cursor-pointer appearance-none"
                                        >
                                            <option value="">All Models</option>
                                            {models.map((m) => (
                                                <option key={m} value={m}>
                                                    {m}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* Desktop Table */}
                        <GenieAcsDesktopTableSection
                            loading={loading}
                            data={data}
                            handleAction={handleAction}
                            handlePageChange={handlePageChange}
                            currentPage={meta?.current_age}
                            totalPages={meta?.total}
                            itemsPerPage={meta?.per_page}
                        />

                        {/* Mobile Card List */}
                        <GenieAcsMobileCardListSection
                            loading={loading}
                            data={data}
                            handleAction={handleAction}
                            handlePageChange={handlePageChange}
                            currentPage={meta?.current_age}
                            totalPages={meta?.total}
                            itemsPerPage={meta?.per_page}
                        />

                    </div>
                </main>

                {/* Modals */}
                {modalConfig.isOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setModalConfig({ ...modalConfig, isOpen: false })} />
                        <div className="relative w-full max-w-sm bg-slate-900 border border-slate-700 rounded-2xl p-6 shadow-2xl transform transition-all scale-100">
                            <h3 className="text-xl font-bold mb-2 text-slate-100">
                                {modalConfig.type === 'delete' ? 'Hapus Pelanggan?' : 'Reboot Perangkat?'}
                            </h3>
                            <p className="text-slate-400 text-sm mb-6">
                                {modalConfig.type === 'delete'
                                    ? `Apakah Anda yakin ingin menghapus data untuk ${modalConfig.data?.customerName} (${modalConfig.data?.customerId})? Tindakan ini tidak dapat dibatalkan.`
                                    : `Kirim perintah reboot ke ${modalConfig.data?.customerName}? Koneksi perangkat akan terputus sementara.`
                                }
                            </p>
                            <div className="flex justify-end gap-3">
                                <button
                                    onClick={() => setModalConfig({ ...modalConfig, isOpen: false })}
                                    className="px-4 py-2 rounded-lg text-sm font-medium text-slate-300 hover:bg-slate-800 transition-colors"
                                >
                                    Batal
                                </button>
                                <button
                                    onClick={confirmAction}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors ${modalConfig.type === 'delete' ? 'bg-red-600 hover:bg-red-500' : 'bg-cyan-600 hover:bg-cyan-500'
                                        }`}
                                >
                                    {modalConfig.type === 'delete' ? 'Hapus' : 'Reboot Sekarang'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </DashboardLayout>
        </>
    );
};